/**
 * Display helpers for interview analysis results.
 *
 * Layered analyses (ai_insights.analysis_version set) can carry null
 * scores, explicit pass/fail results, an UNRESOLVED required-question status
 * and a review-required flag. Historical results have none of these and keep
 * rendering exactly as before.
 */

import { RESUME_MATCH_NOT_COVERED_SUMMARY } from "../lib/constants";

export const REQUIRED_QUESTIONS_LABELS = {
  MET: "Passed",
  PARTIALLY_MET: "Failed",
  NOT_MET: "Failed",
  UNRESOLVED: "Needs review",
  NA: "N/A",
};

export function getRequiredQuestionsLabel(status) {
  if (!status) return "N/A";
  return REQUIRED_QUESTIONS_LABELS[status] || status;
}

export function getRequiredQuestionsFilterValue(status) {
  const label = getRequiredQuestionsLabel(status);
  return label === "N/A" ? "NA" : label;
}

export function getRequiredQuestionsVariant(label) {
  if (label === "Passed") return "success";
  if (label === "Failed") return "failure";
  if (label === "Needs review") return "warning";
  return "gray";
}

export function isLayeredAnalysis(aiInsights) {
  return Boolean(aiInsights?.analysis_version || aiInsights?.assessment_version);
}

export function isReviewRequired(aiInsights) {
  return Boolean(aiInsights?.review_required);
}

/**
 * Why a person has to look at this analysis.
 *
 * Layered results carry `review_reasons`, which holds only what review has to
 * settle — transcript quality notes live in `quality_reasons` and are not
 * shown here. Results stored before that field existed fall back to the older
 * derivation.
 */
export function getReviewReasons(aiInsights) {
  if (Array.isArray(aiInsights?.review_reasons)) {
    return aiInsights.review_reasons.filter(Boolean);
  }
  const reasons = Array.isArray(aiInsights?.quality_reasons)
    ? aiInsights.quality_reasons.filter(Boolean)
    : [];
  if (aiInsights?.knockout_review_required) {
    reasons.unshift("A required (pass/fail) question could not be resolved from the interview.");
  }
  if (aiInsights?.overall_match_rating == null && isLayeredAnalysis(aiInsights)) {
    reasons.push("No question could be scored, so there is no overall rating.");
  }
  return reasons;
}

/** Label for the applicant-response block; layered results carry verbatim text. */
export function getCandidateResponseLabel(aiInsights) {
  return isLayeredAnalysis(aiInsights)
    ? "Applicant Response"
    : "AI Summary of Applicant Response";
}

/**
 * How a per-question score should be shown.
 * @returns {{ kind: "rating", value: number } | { kind: "text", value: string }}
 */
export function getQuestionScoreDisplay(question) {
  if (question?.result === "pass") return { kind: "text", value: "Passed" };
  if (question?.result === "fail") return { kind: "text", value: "Failed" };
  const score = question?.score;
  if (score == null) {
    if (question?.result === "not_answered") return { kind: "text", value: "Not answered" };
    return { kind: "text", value: "Not evaluated" };
  }
  return { kind: "rating", value: score };
}

export function formatQuestionScoreText(question) {
  const display = getQuestionScoreDisplay(question);
  return display.kind === "rating" ? `${display.value}/5` : display.value;
}

export function getScoringRubricEntries(question) {
  const rubric = question?.scoring_rubric;
  if (!rubric?.levels) return [];
  const levels = rubric.type === "pass_fail"
    ? ["pass", "fail"]
    : ["5", "4", "3", "2", "1"];
  return levels.map((level) => ({
    level,
    label: level === "pass" || level === "fail"
      ? `${level[0].toUpperCase()}${level.slice(1)}`
      : level,
    description: rubric.levels[level] || "",
  }));
}

export function formatScoringRubricText(question) {
  const entries = getScoringRubricEntries(question);
  if (!entries.length) {
    return question?.desired_answer || question?.desired_responses || "Not provided";
  }
  return entries
    .map(({ label, description }) => `${label}: ${description}`)
    .join("\n");
}

/** Overall rating for a completed analysis; null only happens on layered results. */
export function getOverallRatingDisplay(aiInsights) {
  const rating = aiInsights?.overall_match_rating;
  if (rating == null) {
    return isLayeredAnalysis(aiInsights)
      ? { kind: "text", value: "Not evaluated" }
      : { kind: "rating", value: 0 };
  }
  return { kind: "rating", value: rating };
}

/**
 * The "Interview vs. Resume" section, or null when it should be hidden.
 * A score shows with its summary; an interview that covered nothing on the
 * resume shows the summary alone; no resume (or no analysis) hides it.
 */
export function getResumeMatchDisplay(aiInsights) {
  const score = aiInsights?.resume_match_score;
  const summary = aiInsights?.resume_match_summary || "";
  if (score != null) return { score, summary };
  if (summary === RESUME_MATCH_NOT_COVERED_SUMMARY) return { score: null, summary };
  return null;
}
